/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, NodeGroupResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type AddNodesToGroupVariables = {
  nodeGroupId: string;
  nodeIds: string[];
};

export type AddNodesToGroupMutationConfig = {
  onSuccess?: (data: NodeGroupResponse, variables: AddNodesToGroupVariables) => void;
  onError?: (error: unknown, variables: AddNodesToGroupVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for adding nodes to an existing node group.
 *
 * @param config.onSuccess - Called after nodes are successfully added
 * @param config.onError - Called if adding nodes fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for adding nodes to a group
 */
export function useAddNodesToGroupMutation(config: AddNodesToGroupMutationConfig) {
  const queryClient = useQueryClient();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isError, error, isPending } = useMutation<
    NodeGroupResponse,
    ApiError,
    AddNodesToGroupVariables
  >({
    mutationFn: ({ nodeGroupId, nodeIds }: AddNodesToGroupVariables) => {
      return mgmtPlaneAPIClient.nodeGroups.addNodesToGroup({
        tenantId: tenantId!,
        nodeGroupId,
        requestBody: { nodeIds },
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId, variables.nodeGroupId] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, mutateAsync, isError, error, isPending };
}
